import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "../lib/api";
import { formatDateTime, formatApiError } from "../lib/format";
import { Card, Loading, EmptyState } from "../components/UI";
import { Send, Edit, ExternalLink, X, MessageSquare, FileText } from "lucide-react";
import { useAuth } from "../lib/auth";

export default function Communications() {
  const { user } = useAuth();
  const [tab, setTab] = useState("send");
  const [templates, setTemplates] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingTpl, setEditingTpl] = useState(null);
  const [selectedTenants, setSelectedTenants] = useState([]);
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [channels, setChannels] = useState({ inapp: true, email: true, whatsapp: false });
  const [waLinks, setWaLinks] = useState([]);

  const reload = async () => {
    const [tpl, t, h] = await Promise.all([
      api.get("/legal-templates"),
      api.get("/tenants").catch(()=>({data:[]})),
      api.get("/communications").catch(()=>({data:[]})),
    ]);
    setTemplates(tpl.data); setTenants(t.data); setHistory(h.data);
    setLoading(false);
  };
  useEffect(() => { reload(); }, []);

  const applyTemplate = (tpl) => {
    setSubject(tpl.title);
    setMessage(tpl.body);
    setTab("send");
  };

  const send = async () => {
    if (selectedTenants.length === 0) { toast.error("Select at least one tenant"); return; }
    if (!subject || !message) { toast.error("Subject and message required"); return; }
    const chs = Object.entries(channels).filter(([,v]) => v).map(([k]) => k);
    try {
      const { data } = await api.post("/communications/send", {
        tenant_ids: selectedTenants,
        subject, message,
        channels: chs,
      });
      toast.success(`Sent to ${data.sent} tenant(s)`);
      setWaLinks(data.whatsapp_links || []);
      setSubject(""); setMessage(""); setSelectedTenants([]);
      reload();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const saveTemplate = async (e) => {
    e.preventDefault();
    const f = new FormData(e.target);
    try {
      await api.patch(`/legal-templates/${editingTpl.id}`, {
        title: f.get("title"),
        body: f.get("body"),
      });
      toast.success("Template saved");
      setEditingTpl(null);
      reload();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  if (loading) return <Loading/>;
  const canEditTemplates = ["admin","lawyer"].includes(user.role);

  return (
    <div className="space-y-6">
      <div className="fade-up flex justify-between items-end">
        <div>
          <h1 className="font-serif text-3xl font-bold text-[#1A2E24]">Communications</h1>
          <p className="text-[#5E7166] text-sm">Send notices and announcements via in-app, email, and WhatsApp.</p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => setTab("send")} className={tab === "send" ? "btn-emerald text-sm" : "btn-outline-emerald text-sm"} data-testid="tab-send">Send</button>
          <button onClick={() => setTab("templates")} className={tab === "templates" ? "btn-emerald text-sm" : "btn-outline-emerald text-sm"} data-testid="tab-templates">Templates</button>
          <button onClick={() => setTab("history")} className={tab === "history" ? "btn-emerald text-sm" : "btn-outline-emerald text-sm"} data-testid="tab-history">History</button>
        </div>
      </div>

      {tab === "send" && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <Card title="Compose Message" className="lg:col-span-2">
            <div className="space-y-3">
              <input value={subject} onChange={e => setSubject(e.target.value)} placeholder="Subject" className="input-modav" data-testid="comm-subject"/>
              <textarea value={message} onChange={e => setMessage(e.target.value)} placeholder="Type your message..." rows={8} className="input-modav" data-testid="comm-message"/>
              <div className="flex gap-4 text-sm flex-wrap">
                <label className="flex items-center gap-2 cursor-pointer"><input type="checkbox" checked={channels.inapp} onChange={e => setChannels({...channels, inapp: e.target.checked})}/> In-app notification</label>
                <label className="flex items-center gap-2 cursor-pointer"><input type="checkbox" checked={channels.email} onChange={e => setChannels({...channels, email: e.target.checked})}/> Email</label>
                <label className="flex items-center gap-2 cursor-pointer"><input type="checkbox" checked={channels.whatsapp} onChange={e => setChannels({...channels, whatsapp: e.target.checked})}/> WhatsApp link</label>
              </div>
              <button onClick={send} className="btn-gold" data-testid="send-comm-btn"><Send size={14}/> Send to {selectedTenants.length} tenant(s)</button>
              {waLinks.length > 0 && (
                <div className="border border-[#E5EBE8] rounded-lg p-3 mt-3">
                  <div className="text-xs font-bold uppercase tracking-wider text-[#5E7166] mb-2">WhatsApp click-to-chat links</div>
                  {waLinks.map((w, i) => (
                    <a key={i} href={w.url} target="_blank" rel="noreferrer" className="block text-sm text-[#0E5C3A] hover:text-[#C9A227] py-1 flex items-center gap-2"><ExternalLink size={12}/> {w.tenant} ({w.phone})</a>
                  ))}
                </div>
              )}
            </div>
          </Card>

          <Card title={`Recipients (${selectedTenants.length})`}>
            <div className="flex gap-2 mb-2">
              <button onClick={() => setSelectedTenants(tenants.map(t => t.id))} className="btn-outline-emerald text-xs">All</button>
              <button onClick={() => setSelectedTenants([])} className="btn-ghost text-xs">Clear</button>
            </div>
            <div className="max-h-96 overflow-y-auto space-y-1">
              {tenants.map(t => (
                <label key={t.id} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-[#F7F9F8] p-2 rounded">
                  <input type="checkbox" checked={selectedTenants.includes(t.id)} onChange={e => {
                    if (e.target.checked) setSelectedTenants([...selectedTenants, t.id]);
                    else setSelectedTenants(selectedTenants.filter(x => x !== t.id));
                  }} data-testid={`recipient-${t.id}`}/>
                  <span>{t.full_name}</span>
                </label>
              ))}
            </div>
          </Card>
        </div>
      )}

      {tab === "templates" && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {templates.map(tpl => (
            <Card key={tpl.id} testid={`template-${tpl.code}`}>
              <div className="flex items-start justify-between mb-2">
                <div>
                  <div className="text-xs font-bold uppercase tracking-wider text-[#C9A227]">{tpl.code}</div>
                  <h3 className="font-serif text-lg font-bold">{tpl.title}</h3>
                </div>
                <div className="flex gap-1">
                  <button onClick={() => applyTemplate(tpl)} className="btn-outline-emerald text-xs"><Send size={12}/> Use</button>
                  {canEditTemplates && <button onClick={() => setEditingTpl(tpl)} className="btn-ghost text-xs"><Edit size={12}/></button>}
                </div>
              </div>
              <pre className="text-xs text-[#5E7166] whitespace-pre-wrap font-sans line-clamp-6">{tpl.body}</pre>
            </Card>
          ))}
        </div>
      )}

      {tab === "history" && (
        <Card className="!p-0 overflow-hidden">
          {history.length === 0 ? <EmptyState icon={MessageSquare} title="No messages sent yet"/> :
            <div className="overflow-x-auto">
              <table className="modav-table">
                <thead><tr><th>Sent</th><th>Tenant</th><th>Subject</th><th>Channels</th></tr></thead>
                <tbody>
                  {history.map(c => (
                    <tr key={c.id}>
                      <td className="text-xs">{formatDateTime(c.sent_at)}</td>
                      <td>{c.tenant?.full_name || "—"}</td>
                      <td>{c.subject}</td>
                      <td className="text-xs">{(c.channels || []).join(", ")}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>}
        </Card>
      )}

      {editingTpl && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="card-modav p-6 w-full max-w-2xl">
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-serif text-xl font-bold">Edit Template — {editingTpl.code}</h3>
              <button onClick={() => setEditingTpl(null)}><X size={20}/></button>
            </div>
            <form onSubmit={saveTemplate} className="space-y-3">
              <input name="title" defaultValue={editingTpl.title} className="input-modav" required/>
              <textarea name="body" defaultValue={editingTpl.body} rows={14} className="input-modav font-mono text-sm" required/>
              <p className="text-xs text-[#5E7166]">Use placeholders like {`{tenant_name}, {unit_name}, {amount}, {due_date}`} — they will not be auto-substituted in MVP; replace before sending.</p>
              <button type="submit" className="btn-gold w-full justify-center">Save Template</button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
